// src/services/api/courierInvoice.js
import { apiClient } from './base';

/**
 * Get column mapping for a specific business courier account.
 * Checks for a businessCourier-specific override first, falls back to company-level mapping.
 * @param {number} businessCourierId - The business courier account ID
 * @returns {Promise<Object>} Column mapping configuration
 */
export const getColumnMappingForCourier = async (businessCourierId) => {
  try {
    const response = await apiClient.get(`/courier-invoices/column-mappings/business-courier/${businessCourierId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Initialize a new courier invoice (before chunk upload)
 * @param {Object} invoiceData - Invoice initialization data
 * @param {string} invoiceData.invoiceNumber - Invoice number from courier
 * @param {number} invoiceData.businessCourierId - Business courier account ID
 * @param {string} invoiceData.invoiceDate - Invoice date (YYYY-MM-DD)
 * @param {number} invoiceData.totalOrders - Total number of orders in invoice
 * @param {Object} invoiceData.metadata - Additional metadata (fileName, fileSize, etc.)
 * @returns {Promise<Object>} Invoice ID, upload token, and chunk size
 */
export const initializeInvoice = async (invoiceData) => {
  try {
    const response = await apiClient.post('/courier-invoices/initialize', invoiceData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Upload a chunk of invoice orders
 * @param {number} invoiceId - The invoice ID
 * @param {Object} chunkData - Chunk upload data
 * @param {string} chunkData.uploadToken - Upload token from initialization
 * @param {number} chunkData.chunkIndex - Current chunk index (0-based)
 * @param {number} chunkData.totalChunks - Total number of chunks
 * @param {Array} chunkData.orders - Array of order objects
 * @returns {Promise<Object>} Chunk processing results
 */
export const uploadChunk = async (invoiceId, chunkData) => {
  try {
    const response = await apiClient.post(`/courier-invoices/${invoiceId}/chunks`, chunkData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Finalize invoice after all chunks uploaded
 * @param {number} invoiceId - The invoice ID
 * @param {Object} finalizeData - Finalization data
 * @param {string} finalizeData.uploadToken - Upload token
 * @param {number} finalizeData.totalChunksSent - Total chunks sent
 * @returns {Promise<Object>} Final invoice results and summary
 */
export const finalizeInvoice = async (invoiceId, finalizeData) => {
  try {
    const response = await apiClient.post(`/courier-invoices/${invoiceId}/finalize`, finalizeData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all courier invoices with filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.search - Search term (invoice number)
 * @param {number} params.businessCourierId - Filter by courier account
 * @param {string} params.status - Filter by status
 * @param {string} params.dateFrom - Start date filter
 * @param {string} params.dateTo - End date filter
 * @returns {Promise<Object>} Paginated invoice list
 */
export const getAllInvoices = async (params = {}) => {
  try {
    const response = await apiClient.get('/courier-invoices', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get invoice by ID
 * @param {number} invoiceId - The invoice ID
 * @returns {Promise<Object>} Invoice details
 */
export const getInvoiceById = async (invoiceId) => {
  try {
    const response = await apiClient.get(`/courier-invoices/${invoiceId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get invoice orders with pagination
 * @param {number} invoiceId - The invoice ID
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.deliveryStatus - Filter by delivery status
 * @param {string} params.orderMatchStatus - Filter by match status
 * @param {string} params.search - Search term
 * @returns {Promise<Object>} Paginated invoice orders
 */
export const getInvoiceOrders = async (invoiceId, params = {}) => {
  try {
    const response = await apiClient.get(`/courier-invoices/${invoiceId}/orders`, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get invoice summary
 * @param {number} invoiceId - The invoice ID
 * @returns {Promise<Object>} Invoice summary statistics
 */
export const getInvoiceSummary = async (invoiceId) => {
  try {
    const response = await apiClient.get(`/courier-invoices/${invoiceId}/summary`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get invoice discrepancies
 * @param {number} invoiceId - The invoice ID
 * @returns {Promise<Object>} List of discrepancies (not found, already invoiced, ineligible)
 */
export const getInvoiceDiscrepancies = async (invoiceId) => {
  try {
    const response = await apiClient.get(`/courier-invoices/${invoiceId}/discrepancies`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Cancel invoice upload (pending or processing state only)
 * @param {number} invoiceId - The invoice ID
 * @returns {Promise<Object>} Success message
 */
export const cancelInvoice = async (invoiceId) => {
  try {
    const response = await apiClient.delete(`/courier-invoices/${invoiceId}/cancel`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete invoice (failed or pending state only)
 * @param {number} invoiceId - The invoice ID
 * @returns {Promise<Object>} Success message
 */
export const deleteInvoice = async (invoiceId) => {
  try {
    const response = await apiClient.delete(`/courier-invoices/${invoiceId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Reprocess failed invoice orders
 * @param {number} invoiceId - The invoice ID
 * @param {Array<number>} orderIds - Optional array of specific order IDs to reprocess
 * @returns {Promise<Object>} Reprocessing results
 */
export const reprocessFailedOrders = async (invoiceId, orderIds = []) => {
  try {
    const response = await apiClient.post(`/courier-invoices/${invoiceId}/reprocess-failed`, { orderIds });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Validate chunk data (dry-run before actual upload)
 * @param {Array} orders - Array of order objects to validate
 * @returns {Promise<Object>} Validation results
 */
export const validateChunk = async (orders) => {
  try {
    const response = await apiClient.post('/courier-invoices/validate-chunk', { orders });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get count of eligible orders for a courier account
 * @param {number} businessCourierId - Business courier account ID
 * @param {Object} filters - Optional date filters
 * @param {string} filters.dateFrom - Start date
 * @param {string} filters.dateTo - End date
 * @returns {Promise<Object>} Count and total value of eligible orders
 */
export const getEligibleOrdersCount = async (businessCourierId, filters = {}) => {
  try {
    const response = await apiClient.get(`/courier-invoices/eligible-orders/${businessCourierId}`, { 
      params: filters 
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};
/**
 * Get flagged invoice orders (status: unresolved | resolved | all)
 */
export const getInvoiceFlags = async (invoiceId, params = {}) => {
  try {
    const response = await apiClient.get(`/courier-invoices/${invoiceId}/flags`, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Re-run flag checks on unresolved matched orders of an invoice
 */
export const recheckInvoiceFlags = async (invoiceId) => {
  try {
    const response = await apiClient.post(`/courier-invoices/${invoiceId}/recheck-flags`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Resolve a flagged invoice order with a note
 */
export const resolveInvoiceFlag = async (invoiceId, invoiceOrderId, note) => {
  try {
    const response = await apiClient.post(
      `/courier-invoices/${invoiceId}/orders/${invoiceOrderId}/resolve-flag`,
      { note }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
